"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  NumberInput,
  SelectInput,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* HSE exposure-points system (Control of Vibration at Work Regulations 2005):
   points for one tool = 2 × a² × t   (a = vibration magnitude m/s², t = trigger
   hours). 100 points = the Exposure Action Value (2.5 m/s² A(8));
   400 points = the Exposure Limit Value (5 m/s² A(8)).
   Combined A(8) for several tools = sqrt(Σ aᵢ²·tᵢ / 8). */

interface ToolRow {
  id: number;
  name: string;
  magnitude: string; // m/s²
  hours: string;
  minutes: string;
}

/* Indicative magnitudes only — measured/manufacturer in-use data should be used
   where available. Ranges based on HSE published examples. */
const PRESETS: { label: string; a: number }[] = [
  { label: "Angle grinder (~5 m/s²)", a: 5 },
  { label: "Hammer drill / combi (~9 m/s²)", a: 9 },
  { label: "Demolition breaker (~12 m/s²)", a: 12 },
  { label: "Disc cutter / cut-off saw (~7 m/s²)", a: 7 },
  { label: "Orbital sander (~4 m/s²)", a: 4 },
  { label: "Impact wrench (~9 m/s²)", a: 9 },
  { label: "Chainsaw (~6 m/s²)", a: 6 },
  { label: "Plate compactor (~6 m/s²)", a: 6 },
  { label: "Needle scaler (~10 m/s²)", a: 10 },
];

let nextId = 1;
function emptyRow(): ToolRow {
  return { id: nextId++, name: "", magnitude: "", hours: "", minutes: "" };
}

function rowExposure(row: ToolRow): { points: number; hours: number; a: number } | null {
  const a = parseFloat(row.magnitude);
  const h = parseFloat(row.hours || "0");
  const m = parseFloat(row.minutes || "0");
  if (!isFinite(a) || a <= 0) return null;
  const hours = (isFinite(h) ? h : 0) + (isFinite(m) ? m : 0) / 60;
  if (hours <= 0) return null;
  return { points: 2 * a * a * hours, hours, a };
}

export function HavsCalculator() {
  const [rows, setRows] = useState<ToolRow[]>([emptyRow()]);
  const [operator, setOperator] = useState("");
  const resultTracked = useRef(false);

  const update = (id: number, patch: Partial<ToolRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const computed = useMemo(() => {
    const perRow = rows.map((r) => ({ row: r, exp: rowExposure(r) }));
    const valid = perRow.filter((p) => p.exp !== null);
    if (valid.length === 0) return null;
    const totalPoints = valid.reduce((s, p) => s + p.exp!.points, 0);
    const sumA2T = valid.reduce((s, p) => s + p.exp!.a * p.exp!.a * p.exp!.hours, 0);
    const a8 = Math.sqrt(sumA2T / 8);
    let level: Verdict;
    let title: string;
    let advice: string;
    if (totalPoints >= 400) {
      level = "veryhigh";
      title = `${Math.round(totalPoints)} points — over the Exposure Limit Value`;
      advice =
        "This exposure exceeds the legal limit of 5 m/s² A(8) (400 points). Work organised like this must not go ahead — cut trigger time, split the work between operatives, or use lower-vibration kit before anyone starts.";
    } else if (totalPoints >= 100) {
      level = "high";
      title = `${Math.round(totalPoints)} points — over the Exposure Action Value`;
      advice =
        "Above 2.5 m/s² A(8) (100 points) you must plan to reduce exposure as low as reasonably practicable and provide health surveillance for regularly exposed workers. Look at lower-vibration tools, job rotation and shorter trigger times.";
    } else {
      level = "low";
      title = `${Math.round(totalPoints)} points — below the Exposure Action Value`;
      advice =
        "Below 100 points the action value isn't reached, but HSE is clear there is no 'safe' level — keep exposure as low as reasonably practicable and watch for tingling or numbness after work.";
    }
    // Points accrued per hour at the current tool mix, and how long until the
    // action (100 pts) and limit (400 pts) values are reached at that rate —
    // the single most useful planning number (HSE / Pulsar pattern).
    const totalHours = valid.reduce((s, p) => s + p.exp!.hours, 0);
    const pointsPerHour = totalHours > 0 ? totalPoints / totalHours : 0;
    const hoursToEav = pointsPerHour > 0 && totalPoints < 100 ? (100 - totalPoints) / pointsPerHour : null;
    const hoursToElv = pointsPerHour > 0 && totalPoints < 400 ? (400 - totalPoints) / pointsPerHour : null;
    // Single biggest contributor — the tool to change first.
    const top = valid.reduce((best, p) => (p.exp!.points > best.exp!.points ? p : best), valid[0]);
    const topId = top.row.id;
    const topPct = totalPoints > 0 ? Math.round((top.exp!.points / totalPoints) * 100) : 0;
    const topName = top.row.name || "The unnamed tool";
    return { perRow: valid, totalPoints, a8, level, title, advice, hoursToEav, hoursToElv, topId, topPct, topName };
  }, [rows]);

  const fmtHours = (h: number) => {
    const whole = Math.floor(h);
    const mins = Math.round((h - whole) * 60);
    return whole > 0 ? `${whole}h ${mins}m` : `${mins}m`;
  };

  // Fire the funnel event once per visit when a result first appears.
  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("havs-calculator", { points: Math.round(computed.totalPoints) });
    }
  }, [computed]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Tools used in a day</h2>
        <p className="mt-1 text-sm text-muted">
          Trigger time is the time the tool is actually running in the operator&apos;s hands — usually far less
          than total task time.
        </p>

        <div className="mt-5 space-y-5">
          {rows.map((row, i) => (
            <div key={row.id} className="rounded-xl border border-line bg-mist/60 p-4">
              <div className="flex items-center justify-between">
                <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Tool {i + 1}</span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setRows((rs) => rs.filter((r) => r.id !== row.id))}
                    className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Tool" className="sm:col-span-2">
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                    <RememberedTextInput
                      store="plant"
                      value={row.name}
                      onChange={(e) => update(row.id, { name: e.target.value })}
                      placeholder="e.g. 9″ angle grinder"
                    />
                    <SelectInput
                      aria-label="Typical tool presets"
                      value=""
                      onChange={(e) => {
                        const preset = PRESETS[parseInt(e.target.value, 10)];
                        if (preset) update(row.id, { name: preset.label.replace(/ \(.*\)$/, ""), magnitude: String(preset.a) });
                      }}
                    >
                      <option value="">Typical values…</option>
                      {PRESETS.map((p, idx) => (
                        <option key={p.label} value={idx}>{p.label}</option>
                      ))}
                    </SelectInput>
                  </div>
                </Field>
                <Field label="Vibration magnitude (m/s²)" hint="From manufacturer in-use data or measurement.">
                  <NumberInput
                    min={0}
                    step={0.1}
                    value={row.magnitude}
                    onChange={(e) => update(row.id, { magnitude: e.target.value })}
                    placeholder="e.g. 5"
                  />
                </Field>
                <Field label="Trigger time">
                  <div className="flex items-center gap-2">
                    <NumberInput min={0} step={1} value={row.hours} onChange={(e) => update(row.id, { hours: e.target.value })} placeholder="0" aria-label="Hours" />
                    <span className="text-sm text-muted">h</span>
                    <NumberInput min={0} step={5} value={row.minutes} onChange={(e) => update(row.id, { minutes: e.target.value })} placeholder="0" aria-label="Minutes" />
                    <span className="text-sm text-muted">min</span>
                  </div>
                </Field>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, emptyRow()])}
            className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            + Add another tool
          </button>
          <Field label="" className="min-w-[200px] flex-1">
            <TextInput value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Operator name (for the printed record)" />
          </Field>
        </div>
        <div className="mt-4">
          <RememberedNamesNote stores={["plant"]} />
        </div>
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="havs-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                HAVS daily exposure record
              </div>
              <h2 className="mt-1 text-lg">
                {operator ? `${operator} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={computed.level} title={computed.title}>
                  {computed.advice}
                </ResultBanner>
              </div>

              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">Tool</th>
                    <th className="py-1.5 pr-2 font-semibold">m/s²</th>
                    <th className="py-1.5 pr-2 font-semibold">Trigger</th>
                    <th className="py-1.5 pr-2 text-right font-semibold">Pts/hr</th>
                    <th className="py-1.5 text-right font-semibold">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.perRow.map(({ row, exp }) => {
                    const isTop = row.id === computed.topId && computed.perRow.length > 1;
                    return (
                    <tr key={row.id} className={`border-b border-line/60 ${isTop ? "bg-emerald-tint" : ""}`}>
                      <td className="py-2 pr-2 text-ink">
                        {row.name || "Unnamed tool"}
                        {isTop && (
                          <span className="ml-1.5 rounded-full bg-emerald-deep px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-white">highest</span>
                        )}
                      </td>
                      <td className={`py-2 pr-2 ${isTop ? "font-semibold" : ""}`}>{exp!.a}</td>
                      <td className="py-2 pr-2">
                        {Math.floor(exp!.hours)}h {Math.round((exp!.hours % 1) * 60)}m
                      </td>
                      {/* points per hour of trigger time = 2a² — shows which tool dominates */}
                      <td className="py-2 pr-2 text-right">{Math.round(2 * exp!.a * exp!.a)}</td>
                      <td className={`py-2 text-right font-semibold ${isTop ? "text-emerald-deep" : "text-ink"}`}>{Math.round(exp!.points)}</td>
                    </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={4} className="py-2 pr-2 font-semibold text-ink">Total daily exposure</td>
                    <td className="py-2 text-right font-display text-lg font-bold text-ink">{Math.round(computed.totalPoints)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[0.78rem]">
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{computed.a8.toFixed(1)} m/s²</div>
                  <div className="text-muted">daily A(8)</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">100 pts</div>
                  <div className="text-muted">action value</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">400 pts</div>
                  <div className="text-muted">limit value</div>
                </div>
              </div>

              {/* Biggest single contributor — change this tool first. */}
              {computed.perRow.length > 1 && (
                <p className="mt-3 rounded-lg border border-emerald/30 bg-emerald-tint px-3 py-2 text-[0.8rem] leading-relaxed text-emerald-deep">
                  <strong>{computed.topName}</strong> is driving <strong>{computed.topPct}%</strong> of today&apos;s exposure
                  — change that tool or cut its trigger time first.
                </p>
              )}

              {/* Remaining trigger-time headroom at the current tool mix. */}
              {(computed.hoursToEav !== null || computed.hoursToElv !== null) && (
                <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[0.8rem] leading-relaxed text-muted">
                  At this tool mix:{" "}
                  {computed.hoursToEav !== null && (
                    <>
                      the <strong className="text-ink">action value</strong> is reached after roughly{" "}
                      <strong className="text-ink">{fmtHours(computed.hoursToEav)}</strong> more trigger time
                    </>
                  )}
                  {computed.hoursToEav !== null && computed.hoursToElv !== null && "; "}
                  {computed.hoursToElv !== null && (
                    <>
                      the <strong className="text-ink">limit value</strong> after roughly{" "}
                      <strong className="text-ink">{fmtHours(computed.hoursToElv)}</strong>
                    </>
                  )}
                  .
                </p>
              )}

              <PrintFooter toolName="HAVS calculator" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="havs-calculator" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Add a tool with a vibration magnitude and trigger time to see the daily exposure points and how
            they compare to the HSE action and limit values.
          </div>
        )}
      </div>
    </div>
  );
}
